import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  TextField, 
  MenuItem,
  Typography,
  IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FileUploader } from "react-drag-drop-files";
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Compressor from "compressorjs";

// Function to detect if dark mode is active from Tailwind's dark class
const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
    },
    "& .MuiPaper-root": {
      width: "90%", 
      maxWidth: "100%",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
      [theme.breakpoints.up("sm")]: {
        width: "90%",
      },
      [theme.breakpoints.up("md")]: {
        width: "500px",
      },
      [theme.breakpoints.up("lg")]: {
        width: "600px",
      },
    },
  };
});


const CustomTextField = ({ sx, multiline, rows, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <TextField
      {...props}
      multiline={multiline}
      rows={rows}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          ...(!multiline && { height: "46px" }),
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-popupIndicator.Mui-focused": { color: "#CD0C02" },
        "& .MuiAutocomplete-inputRoot": {
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
        },
        ...sx,
      }}
    />
  );
};

const fileTypes = ["JPG", "PNG", "JPEG", "PDF"];

export default function ReverseStockModal({ 
  open, 
  onClose, 
  onSubmit,
  isLoading,
  retailers
}) {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    reversedFrom: "",
    amount: "",
    comment: "",
  });
  console.log(retailers, "this is retailers")
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCompressing, setIsCompressing] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const compressFile = (file) => {
    return new Promise((resolve, reject) => {
      if (file.type === 'application/pdf') {
        resolve(file);
        return;
      }

      new Compressor(file, {
        quality: 0.6,
        maxWidth: 1920,
        maxHeight: 1080,
        convertSize: 1000000,
        success(result) {
          const compressedFile = new File(
            [result],
            file.name,
            {
              type: result.type,
              lastModified: new Date().getTime(),
            }
          );
          resolve(compressedFile);
        },
        error(err) {
          console.error('Compression error:', err);
          resolve(file);
        },
      });
    });
  };

  const handleFileChange = async (newFile) => {
    setIsCompressing(true);
    try {
      const compressedFile = await compressFile(newFile);
      setFile(compressedFile);
      
    } catch (error) {
      console.error('File processing error:', error);
      setFile(newFile);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reversedFrom) {
      newErrors.reversedFrom = "Retailer is required";
    }
    
    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    const data = new FormData();
    data.append("reversedFrom", formData.reversedFrom);
    data.append("amount", formData.amount);
    data.append("comment", formData.comment || "");
    if (file) data.append("attachment", file);
    
    onSubmit(data);
  };

  const resetForm = () => {
    setFormData({
      reversedFrom: "",
      amount: "",
      comment: "",
    });
    setFile(null);
    setErrors({});
    setIsCompressing(false);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      onExited={resetForm}
    >
      <div className="px-5 py-3">
        <div className="flex justify-between items-center">
          <Typography variant="h6" className={`my-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t("NewRe")}
          </Typography>
          <IconButton onClick={onClose} disabled={isLoading || isCompressing}>
            <CloseIcon className={isDark ? 'text-gray-400' : 'text-gray-600'} />
          </IconButton>
        </div>

        <div className="w-full grid grid-cols-1 gap-5 mt-4">
          <div>
            <CustomTextField
              select
              fullWidth
              label={t("selectR")}
              name="reversedFrom"
              value={formData.reversedFrom}
              onChange={handleChange}
              error={!!errors.reversedFrom}
              helperText={errors.reversedFrom}
              variant="outlined"
              disabled={isCompressing}
            >
              {retailers.map(retailer => (
                <MenuItem key={retailer.uid} value={retailer.uid} className={isDark ? 'dark:bg-gray-800 dark:text-white' : ''}>
                  {retailer?.user?.username} 
                </MenuItem>
              ))}
            </CustomTextField>
          </div>
          
          <div>
            <CustomTextField
              fullWidth
              type="number"
              label={t("amount")}
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              error={!!errors.amount}
              helperText={errors.amount}
              variant="outlined"
              inputProps={{ min: 0, step: "0.01" }}
              disabled={isCompressing}
            />
          </div>
          
          <div>
            <CustomTextField
              fullWidth
              multiline
              rows={5}
              label={t("commentsO")}
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              variant="outlined"
              disabled={isCompressing}
            />
          </div>
          
          <div>
            <div className="mb-2">
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {t("attachmentO")}
              </label>
              {isCompressing && (
                <div className="text-sm text-blue-600 dark:text-blue-400 mb-1">
                  {t("compressing")}...
                </div>
              )}
            </div>
            
            <FileUploader
              multiple={false}
              handleChange={handleFileChange}
              name="attachment"
              types={fileTypes}
              label="Drag & drop file here or click to browse"
              hoverTitle="Drop here"
              dropMessageStyle={{ border: '2px dashed #CD0C02', borderRadius: '8px' }}
              disabled={isCompressing}
            >
              <div className={`border-2 border-dashed border-[#CD0C02] rounded-lg p-8 text-center cursor-pointer transition ${
                isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
              } ${isCompressing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {file ? (
                  <div className="text-left">
                    <div className={`flex justify-between items-center p-2 rounded ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="flex gap-2">
                        <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {t("select")}
                        </h4>
                        <span className={`truncate max-w-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        disabled={isCompressing}
                      >
                        {t("remove")}
                      </button>
                    </div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t("drag1")}
                    </p>
                    <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {t("support1")}
                    </p>
                    {isCompressing && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                        {t("compressingFile")}
                      </p>
                    )}
                  </>
                )}
              </div>
            </FileUploader>
          </div>
        </div>

        <div className="w-full flex items-center justify-center mt-7 gap-3">
          <button
            onClick={onClose}
            disabled={isLoading || isCompressing}
            className={`py-2 px-6 border rounded-md w-[30%] disabled:opacity-50 transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-400 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isCompressing}
            className={`text-white w-[70%] py-2 px-6 rounded-md disabled:opacity-50 transition ${
              isDark 
                ? 'bg-red-700 hover:bg-red-800' 
                : 'bg-[#CD0C02] hover:bg-[#CD0C02]/90'
            }`}
          >
            {isLoading ? t("submitting...") : t("submit")}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}